import { type Label, LabelType, type ParticipantQrStatus } from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  FloatingPopover,
  MultiSelect,
  type MultiSelectOption,
  NumberedBadge,
  type OnChangeRequest,
} from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaCaretDown, FaFilter } from 'react-icons/fa';

import { FormStack } from '../../components/forms/FormStack';
import { useLabelsQuery } from '../../graphql/hooks/useLabelsQuery';
import { useAppContext } from '../../hooks/useAppContext';
import type { ParticipantQRsFilter } from './hooks/useParticipantQRsFilter';
import { participantQRStatusOptions } from './ParticipantQRStatusTag';

interface Props {
  filter: ParticipantQRsFilter;
}

const ParticipantQRNameFilterControl = (props: Props) => {
  const [value, setValue] = React.useState(props.filter.nameFilter);

  const onChange = React.useCallback(
    (value: string) => {
      setValue(value);
      props.filter.onNameFilter(value);
    },
    [props.filter],
  );

  React.useEffect(() => {
    setValue(props.filter.nameFilter);
  }, [props.filter.nameFilter]);

  return (
    <ChakraV3.Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder='All QR codes'
      size='md'
    />
  );
};
const ParticipantQRStatusFilterControl = (props: Props) => {
  const options: MultiSelectOption<ParticipantQrStatus>[] = Object.values(
    participantQRStatusOptions,
  );

  const selectedValues: MultiSelectOption<ParticipantQrStatus>[] = React.useMemo(() => {
    return (props.filter.statusFilter ?? []).flatMap((status) => {
      return options.find((o) => P.isEqual(o.raw, status)) ?? [];
    });
  }, [props.filter, options]);

  const onChange = React.useCallback(
    (req: OnChangeRequest<ParticipantQrStatus>) => {
      props.filter.onStatusFilter(req.values.map(({ raw }) => raw));
    },
    [props.filter],
  );

  return (
    <MultiSelect
      selectedValues={selectedValues}
      options={options}
      onChange={onChange}
      size='sm'
      placeholder='All statuses'
      optionVariant='tag'
    />
  );
};

const ParticipantQRLabelFilterControl = (props: Props) => {
  const { organization } = useAppContext();
  const { data, isLoading } = useLabelsQuery({
    organizationId: organization.id,
    type: LabelType.ParticipantQr,
  });

  const options: MultiSelectOption<Label>[] = React.useMemo(() => {
    const labels = (data?.organization?.labels as Label[]) ?? [];
    return labels.map((label) => {
      return {
        label: label.name,
        value: label.id,
        raw: label,
        color: label.color,
      };
    });
  }, [data?.organization?.labels]);

  const selectedValues: MultiSelectOption<Label>[] = React.useMemo(() => {
    return (props.filter.labelFilter ?? []).flatMap((label) => {
      return options.find((o) => P.isEqual(o.raw.id, label)) ?? [];
    });
  }, [props.filter, options]);

  const onChange = React.useCallback(
    (req: OnChangeRequest<Label>) => {
      props.filter.onLabelFilter(req.values.map(({ raw }) => raw.id));
    },
    [props.filter],
  );

  return (
    <MultiSelect
      isLoading={isLoading}
      selectedValues={selectedValues}
      options={options}
      onChange={onChange}
      size='sm'
      placeholder='All labels'
      optionVariant='tag'
    />
  );
};

export const ParticipantQRFilterControls = (props: Props) => {
  return (
    <FormStack orientation='horizontal'>
      <ChakraV3.Stack maxW='2xs'>
        <ParticipantQRNameFilterControl filter={props.filter} />
      </ChakraV3.Stack>
      <FloatingPopover
        render={() => (
          <ChakraV3.Stack bg='white' gap={0} w='xs' shadow='2xl' rounded='md' overflow='hidden'>
            <ChakraV3.HStack py={2} px={4} borderBottomWidth='1px'>
              <ChakraV3.Text fontWeight='semibold'>Filters</ChakraV3.Text>
              <ChakraV3.Spacer />
              <NumberedBadge count={props.filter.activeFilterCount} />
            </ChakraV3.HStack>
            <FormStack py={4} px={4} orientation='vertical'>
              <ChakraV3.FormField label='Status'>
                <ParticipantQRStatusFilterControl filter={props.filter} />
              </ChakraV3.FormField>

              <ChakraV3.FormField label='Labels'>
                <ParticipantQRLabelFilterControl filter={props.filter} />
              </ChakraV3.FormField>

              <ChakraV3.Separator />

              <ChakraV3.Button
                w='fit-content'
                size='sm'
                variant='outline'
                disabled={props.filter.activeFilterCount === 0}
                onClick={props.filter.reset}
                colorPalette='brand'
              >
                Reset
              </ChakraV3.Button>
            </FormStack>
          </ChakraV3.Stack>
        )}
      >
        <ChakraV3.Button size='sm' variant='outline' lineHeight='unset'>
          <FaFilter />
          <ChakraV3.HStack>
            Filter
            <ChakraV3.Stack w='28px'>
              <NumberedBadge count={props.filter.activeFilterCount} />
            </ChakraV3.Stack>
          </ChakraV3.HStack>
          <FaCaretDown />
        </ChakraV3.Button>
      </FloatingPopover>
    </FormStack>
  );
};
